import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/cn"

const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-[8px] text-[12px] font-medium transition-all duration-150 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-45 active:scale-[0.985]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white shadow-[0_1px_2.5px_rgba(0,122,255,0.35),inset_0_0.5px_0_rgba(255,255,255,0.2)] hover:bg-[#0071eb] active:bg-[#0066d6]',
        secondary: 'border border-black/[0.08] bg-surface text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]',
        ghost: 'text-text hover:bg-black/[0.05]',
        destructive: 'bg-danger text-white shadow-[0_1px_2.5px_rgba(255,59,48,0.3)] hover:bg-[#eb3a30]',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-3.5',
        sm: 'h-7 px-2.5 text-[11.5px]',
        lg: 'h-9 px-4 text-[13px]',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
